import React, { Component } from "react";
import { connect } from "react-redux";
import { NavLink, withRouter } from "react-router-dom";
import { PropTypes } from "prop-types";
import { Menu, Image, Icon } from "semantic-ui-react";
import logo from "../../assets/images/sherpa-logo-white.png";

import { Media, MediaContextProvider } from "../../styles/Media.js";

import { AT_TYPES } from "../../constants/entity.js";
import { OPERATIONS } from "../../constants/operations.js";
import { ROUTES } from "../../constants/routing";

import coreActions from "../../state/reducers/core/coreActions.js";
import { resetErrors } from "../../state/reducers/api/apiActions.js";
import { doLogout } from "../../state/reducers/authentication/authenticationActions.js";
import { operationEnabled } from "../../utils/operations.js";

import { selectTestingEnvironment } from "../../state/reducers/config/configSelectors.js";
import {
  selectEndpointByType,
  selectOperationAvailability
} from "../../state/reducers/endpoints/endpointSelector.js";
import { selectCopy } from "../../state/reducers/locale/copySelector.js";

class MainNavigation extends Component {
  static propTypes = {
    testingEnvironment: PropTypes.string,
    doLogout: PropTypes.func.isRequired,
    navCopyKey: PropTypes.string.isRequired,
    copyKey: PropTypes.string.isRequired,
    copy: PropTypes.func.isRequired
  };

  static defaultProps = {
    navCopyKey: "navigation",
    copyKey: "main_navigation"
  };

  state = {
    visible: true
  };

  // -- HANDLERS -- //

  toggleMenu = () => {
    const { visible } = this.state;
    this.setState({ visible: !visible });
  };

  // -- CLICK HANDLERS -- //

  doResets = () => {
    this.props.resetWatchers();
    this.props.resetErrors();
  };

  doLogout = () => {
    const { doLogout, history } = this.props;
    history.push("/");
    doLogout();
  };

  doContactUs = () => {
    const { copy } = this.props;

    window.location.href = `mailto:${copy(
      `dashboard.welcome.admin.email_address`
    )}`;
  };

  doManageCookies = () => {
    Cookiebot.renew();
  };

  // -- RENDER -- //

  renderEndpointItem = (endpoint, endpointCopyKey, search) => {
    const { navCopyKey, copy } = this.props;

    if (!endpoint) return null;

    const listAvailable = operationEnabled(
      OPERATIONS.LIST,
      endpoint.enabledOperations
    );

    if (!listAvailable) return null;

    return (
      <Menu.Item
        as={NavLink}
        onClick={this.doResets}
        to={`${endpoint["@id"]}${search ? `?${search}` : ""}`}
      >
        {copy(`${navCopyKey}.${endpointCopyKey}`)}
      </Menu.Item>
    );
  };

  renderMenu(useVisible) {
    const {
      name,
      testingEnvironment,
      projectEndpoint,
      scheduleEndpoint,
      createImageAvailability,
      imagesEndpoint,
      invoiceEndpoint,
      inviteEndpoint,
      navCopyKey,
      copyKey,
      copy
    } = this.props;

    const { visible } = this.state;

    // build className
    let className = "main-navigation";
    if (useVisible) {
      className = `${className} ${visible ? "slide-in" : "slide-out"}`;
    }
    if (testingEnvironment) className = `${className} testing_environment`;

    return (
      <div className={className}>
        <Media lessThan="tablet">
          <Icon
            className="menuToggle"
            name="bars"
            inverted
            onClick={this.toggleMenu}
          />
        </Media>
        <div className="wrapper">
          <div className="menu-wrapper">
            <Menu vertical size="small" inverted>
              <Image
                as={NavLink}
                to="/dashboard"
                onClick={this.doResets}
                className="sherpa-logo"
                src={logo}
                size="tiny"
              />
              {testingEnvironment && (
                <p className="environment">
                  {testingEnvironment.toUpperCase()}
                </p>
              )}
              <p className="sherpa-greeting">
                <NavLink onClick={this.doResets} to={ROUTES.DASHBOARD}>{`${copy(
                  `${copyKey}.greeting`
                )} ${name}`}</NavLink>
              </p>

              <Menu.Item
                as={NavLink}
                onClick={this.doResets}
                to={ROUTES.DASHBOARD}
              >
                {copy(`${navCopyKey}.dashboard`)}
              </Menu.Item>

              {this.renderEndpointItem(projectEndpoint, "projects")}
              {this.renderEndpointItem(scheduleEndpoint, "schedules")}
              {createImageAvailability.available &&
                this.renderEndpointItem(imagesEndpoint, "images")}
              {this.renderEndpointItem(invoiceEndpoint, "invoices")}
              {this.renderEndpointItem(inviteEndpoint, "invites")}

              <Menu.Item as={NavLink} onClick={this.doResets} to="/account">
                {copy(`${navCopyKey}.account`)}
              </Menu.Item>

              <Menu.Item href={ROUTES.DOCUMENTATION} target="_blank">
                {copy(`${navCopyKey}.documentation`)}
              </Menu.Item>

              <Menu.Item href={ROUTES.SUPPORT} target="_blank">
                {copy(`${navCopyKey}.support`)}
              </Menu.Item>

              <Menu.Item onClick={this.doContactUs}>
                {copy(`${navCopyKey}.contact_us`)}
              </Menu.Item>

              <Menu.Item onClick={this.doLogout}>
                {copy(`${navCopyKey}.logout`)}
              </Menu.Item>
            </Menu>

            <div className="link-menu">
              <a onClick={this.doManageCookies}>
                {copy(`${navCopyKey}.cookie_consent`)}
              </a>
              <br />
              <a href="/cookies.html">{copy(`${navCopyKey}.cookie_policy`)}</a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    return (
      <MediaContextProvider>
        <Media lessThan="tablet">{this.renderMenu(true)}</Media>
        <Media greaterThanOrEqual="tablet">{this.renderMenu()}</Media>
      </MediaContextProvider>
    );
  }
}

const mapStateToProps = state => ({
  name: state.account.data.firstName || state.authentication.name,
  testingEnvironment: selectTestingEnvironment(state),
  projectEndpoint: selectEndpointByType(state)(AT_TYPES.PROJECT),
  scheduleEndpoint: selectEndpointByType(state)(AT_TYPES.SCHEDULE),
  imagesEndpoint: selectEndpointByType(state)(AT_TYPES.IMAGE),
  createImageAvailability: selectOperationAvailability(state)(
    AT_TYPES.IMAGE,
    OPERATIONS.CREATE
  ),
  invoiceEndpoint: selectEndpointByType(state)(AT_TYPES.INVOICE),
  inviteEndpoint: selectEndpointByType(state)(AT_TYPES.INVITE),
  copy: selectCopy(state)
});

const mapDispatchToProps = dispatch => ({
  resetWatchers: () => dispatch(coreActions.resetWatchers()),
  resetErrors: () => dispatch(resetErrors()),
  doLogout: () => dispatch(doLogout())
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(MainNavigation)
);
